/**
 * EE 統一設定頁
 *
 * 三個 Tab：
 *   1. Email 設定    — SMTP + 測試寄信按鈕
 *   2. 排程設定      — watchdog / 警示 / 保留天數等
 *   3. Google OAuth  — Client ID / Secret（生活中心 Google 同步用）
 *
 * 對應後端：/api/ee/settings 與 /api/ee/mailer/test
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Tabs, Tab, TextField, Switch, FormControlLabel,
  Button, Typography, Divider, Chip, Alert,
} from '@mui/material';
import { Save as SaveIcon, Send as SendIcon } from '@mui/icons-material';

import FunctionPageHeader from '../../components/FunctionPageHeader';
import { useMessage } from '../../contexts/MessageContext';
import {
  EeSettings, MailerConfig, SchedulerConfig, GoogleOAuthConfig,
  getSettings, updateMailerSettings, updateSchedulerSettings, testMailer,
  updateGoogleOAuthSettings,
} from '../../services/eeSettingsService';
import { formatLocal } from '../../utils/dateHelper';

const formatDateTime = (iso?: string | null): string => {
  if (!iso) return '—';
  try { return formatLocal(iso); } catch { return iso; }
};

// ============================================================
// Tab 1: Email 設定
// ============================================================

const MailerTab: React.FC<{ settings: EeSettings | null; reload: () => void }> = ({ settings, reload }) => {
  const { showSuccess, showApiError } = useMessage();
  const [form, setForm] = useState<MailerConfig>({
    smtp_host: '', smtp_port: 587, smtp_user: '', smtp_password: '',
    smtp_from: '', smtp_use_tls: true, is_enabled: false,
    last_test_at: null, last_test_status: null, last_test_message: null,
  });
  const [pwdPlaceholder, setPwdPlaceholder] = useState(false);

  useEffect(() => {
    if (settings?.config?.mailer) {
      const m = settings.config.mailer;
      setForm(m);
      setPwdPlaceholder(m.smtp_password === '***');
    }
  }, [settings]);

  const handleSave = async () => {
    try {
      await updateMailerSettings(form);
      showSuccess('SYS030006');
      reload();
    } catch (err) { showApiError(err); }
  };

  const handleTest = async () => {
    try {
      const resp = await testMailer();
      // lint-msg-allow: test mail uses dynamic API response message, non-transactional notification
      showSuccess(`測試信已寄出：${resp.message}`);
      reload();
    } catch (err) { showApiError(err); }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 600 }}>
      <FormControlLabel label="啟用 Email 發送（EE 警示與通知必要）"
        control={<Switch checked={form.is_enabled}
                         onChange={e => setForm({ ...form, is_enabled: e.target.checked })} />} />

      <TextField label="SMTP Host" value={form.smtp_host}
                 onChange={e => setForm({ ...form, smtp_host: e.target.value })} size="small" />
      <TextField label="SMTP Port" type="number" value={form.smtp_port}
                 onChange={e => setForm({ ...form, smtp_port: parseInt(e.target.value || '587') })}
                 size="small" />
      <TextField label="SMTP User" value={form.smtp_user}
                 onChange={e => setForm({ ...form, smtp_user: e.target.value })} size="small" />
      <TextField label="SMTP Password" type="password"
                 value={form.smtp_password}
                 placeholder={pwdPlaceholder ? '(保留既有密碼，留空白改值)' : ''}
                 onChange={e => setForm({ ...form, smtp_password: e.target.value })}
                 size="small"
                 helperText={pwdPlaceholder ? '欲變更密碼請輸入新密碼；欲保留原密碼請維持空白或 ***' : undefined} />
      <TextField label="寄件人（From）" value={form.smtp_from}
                 onChange={e => setForm({ ...form, smtp_from: e.target.value })}
                 size="small" helperText="例：Base AP Alerts <alerts@example.com>" />
      <FormControlLabel label="使用 STARTTLS"
        control={<Switch checked={form.smtp_use_tls}
                         onChange={e => setForm({ ...form, smtp_use_tls: e.target.checked })} />} />

      <Divider sx={{ my: 1 }} />
      <Box>
        <Typography variant="caption" color="text.secondary">最近測試</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {form.last_test_status === 'success' && <Chip label="成功" size="small" color="success" />}
          {form.last_test_status === 'failed' && <Chip label="失敗" size="small" color="error" />}
          {!form.last_test_status && <Chip label="尚未測試" size="small" />}
          <Typography variant="body2">{formatDateTime(form.last_test_at)}</Typography>
        </Box>
        {form.last_test_message && (
          <Typography variant="caption" sx={{ mt: 0.5, display: 'block' }}>
            {form.last_test_message}
          </Typography>
        )}
      </Box>

      <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
        <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave}>儲存</Button>
        <Button variant="outlined" startIcon={<SendIcon />} onClick={handleTest}>測試寄信</Button>
      </Box>

      <Alert severity="info" sx={{ mt: 2 }}>
        測試信寄送至系統設定中的「系統管理聯絡人 Email」（sys_profiles.sys_mana_email）。
      </Alert>
    </Box>
  );
};

// ============================================================
// Tab 2: 排程設定
// ============================================================

const SchedulerTab: React.FC<{ settings: EeSettings | null; reload: () => void }> = ({ settings, reload }) => {
  const { showSuccess, showApiError } = useMessage();
  const [form, setForm] = useState<SchedulerConfig>({
    trigger_rest_sec: 10, max_concurrent_repeatable: 10,
    fail_alert_threshold: 3, overdue_alert_minutes: 30,
    alert_enabled: true, watchdog_interval_sec: 60,
    watchdog_lookahead_sec: 120, runs_retention_days: 90,
  });

  useEffect(() => {
    if (settings?.config?.scheduler) {
      setForm(settings.config.scheduler);
    }
  }, [settings]);

  const handleSave = async () => {
    try {
      await updateSchedulerSettings(form);
      showSuccess('SYS030006');
      reload();
    } catch (err) { showApiError(err); }
  };

  const numberField = (label: string, field: keyof SchedulerConfig, helper?: string) => (
    <TextField label={label} type="number" value={form[field] as number}
               onChange={e => setForm({ ...form, [field]: parseInt(e.target.value || '0') })}
               size="small" helperText={helper} />
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 600 }}>
      <FormControlLabel label="啟用警示器（關閉後不寄任何失敗/逾期警示）"
        control={<Switch checked={form.alert_enabled}
                         onChange={e => setForm({ ...form, alert_enabled: e.target.checked })} />} />

      <Divider><Typography variant="caption">觸發器</Typography></Divider>
      {numberField('觸發器間隔（秒）', 'trigger_rest_sec',
                   '同佇列兩項目之間的休息秒數，預設 10')}
      {numberField('可重複最大並行數', 'max_concurrent_repeatable',
                   'repeatable 類別任務最多同時執行幾個，預設 10')}

      <Divider><Typography variant="caption">看門狗</Typography></Divider>
      {numberField('看門狗執行間隔（秒）', 'watchdog_interval_sec',
                   '允許 30~300，預設 60；變更會即時生效')}
      {numberField('看門狗向前看（秒）', 'watchdog_lookahead_sec',
                   '須 ≥ 2 × interval，預設 120')}

      <Divider><Typography variant="caption">警示</Typography></Divider>
      {numberField('連續失敗閾值（次）', 'fail_alert_threshold',
                   '連續失敗達此次數時寄警示，預設 3')}
      {numberField('逾期警示分鐘數', 'overdue_alert_minutes',
                   'next_run_at 逾期此分鐘數時寄警示，預設 30')}

      <Divider><Typography variant="caption">紀錄保留</Typography></Divider>
      {numberField('執行紀錄保留天數', 'runs_retention_days',
                   '資安稽核要求下限 30 天，預設 90')}

      <Box sx={{ mt: 2 }}>
        <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave}>儲存</Button>
      </Box>
    </Box>
  );
};

// ============================================================
// Tab 3: Google OAuth 設定
// ============================================================

const GoogleOAuthTab: React.FC<{ settings: EeSettings | null; reload: () => void }> = ({ settings, reload }) => {
  const { showSuccess, showApiError } = useMessage();
  const [form, setForm] = useState<GoogleOAuthConfig>({
    client_id: '',
    client_secret: '',
    redirect_uri: '',
    is_enabled: false,
    last_test_at: null,
    last_test_status: null,
    calendar_api_key: '',
  });
  const [secretMasked, setSecretMasked] = useState(false);
  const [apiKeyMasked, setApiKeyMasked] = useState(false);

  useEffect(() => {
    const cfg = settings?.config?.google_oauth;
    if (cfg) {
      setForm({ ...cfg, calendar_api_key: cfg.calendar_api_key ?? '' });
      setSecretMasked(cfg.client_secret === '***');
      setApiKeyMasked(!!cfg.calendar_api_key);
    }
  }, [settings]);

  const handleSave = async () => {
    try {
      await updateGoogleOAuthSettings(form);
      showSuccess('SYS030006');
      reload();
    } catch (err) { showApiError(err); }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 600 }}>
      <Alert severity="info">
        在 Google Cloud Console 建立 OAuth 2.0 用戶端 ID（Web application），
        並將 Redirect URI 加入「已授權的重新導向 URI」。
      </Alert>

      <FormControlLabel label="啟用 Google 整合（顯示生活中心 Google 同步功能）"
        control={<Switch checked={form.is_enabled}
                         onChange={e => setForm({ ...form, is_enabled: e.target.checked })} />} />

      <TextField
        label="Client ID"
        value={form.client_id}
        onChange={e => setForm({ ...form, client_id: e.target.value })}
        size="small"
        fullWidth
        helperText="格式：xxx.apps.googleusercontent.com"
      />
      <TextField
        label="Client Secret"
        type="password"
        value={form.client_secret}
        onChange={e => { setForm({ ...form, client_secret: e.target.value }); setSecretMasked(false); }}
        size="small"
        fullWidth
        helperText={secretMasked ? '欲變更請輸入新的 Secret；維持 *** 則保留原值' : undefined}
      />
      <TextField
        label="Redirect URI"
        value={form.redirect_uri}
        onChange={e => setForm({ ...form, redirect_uri: e.target.value })}
        size="small"
        fullWidth
        helperText="例：https://your-domain.com/api/ee_my_contacts/google/callback"
      />

      <Divider sx={{ my: 1 }} />
      <Alert severity="info" sx={{ fontSize: '0.8rem' }}>
        Google Calendar API Key 用於在行事曆顯示公開假日，與上方 OAuth 無關。
        請在 Google Cloud Console → 憑證 → 建立 API 金鑰（限制：Google Calendar API）。
      </Alert>
      <TextField
        label="Google Calendar API Key"
        type="password"
        value={form.calendar_api_key}
        onChange={e => { setForm({ ...form, calendar_api_key: e.target.value }); setApiKeyMasked(false); }}
        size="small"
        fullWidth
        helperText={apiKeyMasked ? '已設定，欲變更請輸入新的 API Key（顯示為 ●●● 為正常）' : '用於顯示公開假日日曆（不需要 OAuth）'}
      />

      <Divider sx={{ my: 1 }} />
      <Box>
        <Typography variant="caption" color="text.secondary">最近測試</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {form.last_test_status === 'success' && <Chip label="成功" size="small" color="success" />}
          {form.last_test_status === 'failed' && <Chip label="失敗" size="small" color="error" />}
          {!form.last_test_status && <Chip label="尚未測試" size="small" />}
          <Typography variant="body2">{formatDateTime(form.last_test_at)}</Typography>
        </Box>
      </Box>

      <Box sx={{ mt: 1 }}>
        <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave}>儲存</Button>
      </Box>
    </Box>
  );
};

// ============================================================
// Main
// ============================================================

const EeSettingsPage: React.FC = () => {
  const [tab, setTab] = useState(0);
  const [settings, setSettings] = useState<EeSettings | null>(null);
  const { showApiError } = useMessage();

  const reload = useCallback(async () => {
    try {
      setSettings(await getSettings());
    } catch (err) { showApiError(err); }
  }, [showApiError]);

  useEffect(() => { reload(); }, [reload]);

  return (
    <Box>
      <FunctionPageHeader funcCode="ee_settings" fallback="EE 設定" />
      <Tabs value={tab} onChange={(_, v) => setTab(v)}
            sx={{ mt: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Tab label="Email 設定" />
        <Tab label="排程設定" />
        <Tab label="Google OAuth" />
      </Tabs>
      <Box sx={{ mt: 3 }}>
        {tab === 0 && <MailerTab settings={settings} reload={reload} />}
        {tab === 1 && <SchedulerTab settings={settings} reload={reload} />}
        {tab === 2 && <GoogleOAuthTab settings={settings} reload={reload} />}
      </Box>
    </Box>
  );
};

export default EeSettingsPage;